/**
 * Spring and gesture-projection maths for the deck, specified the way Apple and Material
 * specify motion rather than as hand-tuned constants.
 *
 * WHY THIS EXISTS. The deck's springs used to be four magic numbers: (340, 40) to spring a
 * card home and (240, 31) to fling it away. Nobody could say what they meant, so nobody could
 * tell whether they were right, and they were not: the exit measured 632ms whether the reader
 * threw the card hard or barely nudged it, because a fixed stiffness pulling to a fixed target
 * takes a fixed time. An interface that takes the same time no matter how hard you push it is
 * the definition of unresponsive.
 *
 * Apple's WWDC23 "Animate with springs" reparametrises the same physics into two numbers a
 * human can actually reason about: a PERCEPTUAL DURATION (roughly when the motion reads as
 * done, distinct from the unpredictable settling time) and a BOUNCE (0 = critically damped,
 * no overshoot). For unit mass:
 *
 *     stiffness = (2 * PI / duration)^2
 *     damping   = 4 * PI / (duration * (1 + bounce))     for bounce >= 0
 *     damping   = 4 * PI * (1 - bounce) / duration       for bounce <= 0
 *
 * At bounce = 0 both branches give 4*PI/duration, which is exactly 2*sqrt(stiffness), i.e.
 * critical damping. So the parametrisation is continuous and "bounce 0" provably cannot ring.
 *
 * The durations we pass in come from the research, not from taste. Material puts the mobile
 * sweet spot at 200-300ms; Apple's HIG lands around 250ms for navigational movement; the
 * Doherty threshold puts the ceiling for "the machine is keeping up with me" at 400ms. A
 * card-to-card transition is navigational, so it belongs at the short end.
 */

/** Spring constants for a unit-mass semi-implicit Euler integrator. */
export interface SpringSpec {
  stiffness: number;
  damping: number;
}

/**
 * Build a spring from a perceptual duration (seconds) and a bounce.
 *
 * `bounce` 0 is critically damped: the fastest arrival that never crosses the target. Positive
 * bounce overshoots (Material calls these "spatial" springs, and allows it for position, size
 * and shape). Negative bounce is overdamped, which reads as sluggish and is rarely what you
 * want. Material's rule is worth keeping: things that MOVE may bounce, things that FADE must
 * not, because an overshooting opacity has to travel back through a wrong value in view.
 */
export function springFor(durationSec: number, bounce = 0): SpringSpec {
  const d = Math.max(durationSec, 0.0001);
  const stiffness = (2 * Math.PI / d) ** 2;
  const damping =
    bounce >= 0
      ? (4 * Math.PI) / (d * (1 + bounce))
      : (4 * Math.PI * (1 - bounce)) / d;
  return { stiffness, damping };
}

/** The damping that makes a given stiffness critically damped, for asserting "this cannot ring". */
export function criticalDamping(stiffness: number): number {
  return 2 * Math.sqrt(stiffness);
}

/**
 * How long the exit should take, given how hard the card was thrown.
 *
 * A spring alone cannot express this: pulling to a fixed target with a fixed stiffness takes a
 * fixed time, so the shipped deck exited in 632ms whether the release velocity was 0.05 px/ms
 * or 0.3. Here the remaining distance and the finger's own speed set the duration, then it is
 * clamped into the range the research supports, so a hard flick genuinely leaves faster and a
 * gentle push still never dawdles.
 *
 * Clamped to [MIN, MAX] rather than left open because below ~120ms a transition stops being
 * perceived as movement at all (it reads as a cut, and the reader loses the thread of which
 * card went where), and beyond ~380ms it crosses the Doherty threshold and the app feels like
 * it is thinking.
 *
 * MIN raised 0.13 -> 0.18 (2026-08-12): 130ms sat right on the edge the paragraph above
 * warns about, and on device a hard flick's page turn read as a cut ("the next snip comes
 * immediately"). ViewPager's own velocity-computed settles land in 200-350ms; 180ms of exit
 * plus the arrival refine lands the whole turn in that band while still letting a flick feel
 * faster than a push.
 */
export const EXIT_MIN_SEC = 0.18;
export const EXIT_MAX_SEC = 0.34;

/**
 * The exit spring is deliberately NOT critically damped.
 *
 * Bounce 0 minimises overshoot, which is the right choice when a card has to arrive somewhere
 * the reader can see. On the way OUT there is nothing to arrive at: the target is past the edge
 * of the screen, so overshoot is invisible by construction. What bounce 0 does cost is real,
 * because critical damping at a short duration means a very high damping coefficient, and that
 * coefficient brakes the reader's own throw. Measured: a hard flick left in 216ms under the old
 * spring but 240ms under a critically damped replacement, i.e. the "better" spring made a fast
 * swipe slower by fighting it.
 *
 * A modest positive bounce lowers the damping, so the flick keeps its momentum and the spring
 * only has to supply what the finger did not. This is Material's spatial-versus-effect rule
 * arriving at the same answer from the other direction: things that MOVE may overshoot.
 */
export const EXIT_BOUNCE = 0.2;

export function exitDurationSec(remainingPx: number, velocityPxPerMs: number): number {
  const distance = Math.abs(remainingPx);
  const speed = Math.abs(velocityPxPerMs);
  // Time the finger itself would need to cover what is left, with a floor on the speed so a
  // near-zero release does not divide out to an absurd duration before clamping.
  const ballistic = distance / Math.max(speed, 0.05) / 1000;
  // Blend toward the ballistic time rather than adopting it outright: matching the finger
  // exactly makes a violent flick disappear in one frame, which loses the hand-off entirely.
  const blended = EXIT_MIN_SEC + (ballistic - EXIT_MIN_SEC) * 0.45;
  return Math.min(Math.max(blended, EXIT_MIN_SEC), EXIT_MAX_SEC);
}

/**
 * A release animation, pre-computed so the COMPOSITOR can run it instead of JavaScript.
 *
 * The rAF spring integrator shares the main thread with everything else the app does at
 * release time: the ambient-light fade, image decodes, and, right at hand-over, React
 * re-rendering the whole shell to mount the next card. Any of those overrunning a frame
 * budget skips a frame OF THE MOVING CARD, which the eye reads instantly as a stutter. A
 * CSS transform/opacity transition runs on the compositor thread, so the card keeps
 * gliding at the panel's own refresh rate no matter what the main thread is chewing on.
 * This is the actual mechanism behind "native apps feel smoother": their animators do not
 * live on the thread that does the work.
 *
 * A transition needs a timing function, and a spring is not a cubic-bezier, so the same
 * semi-implicit Euler integration the rAF animator runs (identical 8ms substep, identical
 * settle test) is run HERE, ahead of time, and its trajectory is baked into a CSS
 * `linear()` easing. Same physics, same duration, same hand-over point; only the thread
 * changes. `settledWhen` cuts the plan exactly where the live spring would have handed
 * over (the exit uses it to stop at the screen edge), so `endX` is where the element
 * really stops, not the spring's asymptotic target.
 *
 * Returns null when the journey is too short to animate (the caller falls back to the
 * rAF spring, which converges immediately for those).
 */
export interface GlidePlan {
  durationMs: number;
  /** Where the trajectory actually ends (at settledWhen, or converged near the target). */
  endX: number;
  /** CSS `linear(...)` timing function tracing the spring's own trajectory. */
  easing: string;
}

export function planGlide(
  fromX: number,
  to: number,
  v0PxPerMs: number,
  spec: SpringSpec,
  settledWhen?: (x: number) => boolean,
): GlidePlan | null {
  let x = fromX;
  let v = v0PxPerMs * 1000;
  const H = 0.008; // the rAF animator's own substep, so both trajectories are identical
  const xs: number[] = [x];
  for (let i = 0; i < 250; i++) {
    v += (spec.stiffness * (to - x) - spec.damping * v) * H;
    x += v * H;
    xs.push(x);
    if (settledWhen?.(x) || (Math.abs(to - x) < 0.5 && Math.abs(v) < 20)) break;
  }
  const endX = xs[xs.length - 1];
  const span = endX - fromX;
  if (xs.length < 3 || Math.abs(span) < 2) return null;
  // ~24 stops keeps the easing string small; between stops the browser interpolates
  // linearly, and 8-16ms segments of a smooth trajectory are visually indistinguishable
  // from the integrator's own frames.
  const stride = Math.max(1, Math.ceil((xs.length - 1) / 24));
  const stops: string[] = [];
  for (let i = 0; i < xs.length - 1; i += stride) {
    stops.push(String(Math.round(((xs[i] - fromX) / span) * 10000) / 10000));
  }
  stops.push("1");
  return {
    durationMs: Math.round((xs.length - 1) * H * 1000),
    endX,
    easing: `linear(${stops.join(", ")})`,
  };
}

/**
 * Whether this engine can play a GlidePlan. `linear()` easing shipped in Chrome 113 and
 * Safari 17.2; on anything older the whole transition declaration would be dropped and the
 * card would TELEPORT, so the caller must keep the rAF spring as the fallback rather than
 * degrade. Evaluated once: engines do not gain CSS features mid-session.
 */
export const supportsLinearEasing: boolean =
  typeof CSS !== "undefined" &&
  typeof CSS.supports === "function" &&
  CSS.supports("transition-timing-function", "linear(0, 0.5, 1)");
